import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  catchError,
  tap,
  forkJoin,
  map,
  from,
} from 'rxjs';
import { environment } from '../../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../../core/services/error-handling.service';
import {
  AttachmentResponse,
  MediaTypeResponse,
  UploadResponse,
  Attachment,
  AttachmentResponseWithPlanType,
} from '../../insights.interface';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class StudentAttachmentService {
  private readonly BASE_URL = environment.apiUrl;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);

  private attachmentsSubject = new BehaviorSubject<AttachmentResponse | null>(
    null
  );
  readonly attachments$ = this.attachmentsSubject.asObservable();

  uploadAttachment(
    caseId: number,
    folderId: number,
    planTypeId: number | null,
    name: string,
    description: string,
    mediaTypeId: string,
    file: File,
    caseAttachmentId?: number,
    isQuestionnairePDF = false
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('CaseId', caseId.toString());
    formData.append('folderId', folderId.toString());
    formData.append('planTypeId', planTypeId ? planTypeId.toString() : '');
    formData.append('Name', name);
    formData.append('Description', description);
    formData.append('MediaTypeId', mediaTypeId);
    formData.append('File', file);
    formData.append('isQuestionnairePDF', isQuestionnairePDF.toString());
    if (caseAttachmentId) {
      formData.append('caseAttachmentId', caseAttachmentId.toString());
    }

    return this.http
      .post<UploadResponse>(`${this.BASE_URL}/CaseAttachment/upload`, formData)
      .pipe(
        // tap(() => this.refreshAttachments(caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAttachments(caseId: number): Observable<AttachmentResponse> {
    return this.http
      .get<AttachmentResponse>(`${this.BASE_URL}/CaseAttachment/list`, {
        params: { caseId: caseId.toString() },
      })
      .pipe(
        tap(response => this.attachmentsSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAttachmentsWithPlanTypeId(
    caseId: number
  ): Observable<AttachmentResponseWithPlanType> {
    return this.http
      .get<AttachmentResponseWithPlanType>(
        `${this.BASE_URL}/CaseAttachment/get-list-by-plantypeid-caseid`,
        {
          params: { caseId: caseId.toString() },
        }
      )
      .pipe(
        tap(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateAttachment(
    id: number,
    caseId: number,
    folderId: number,
    planTypeId: number | null,
    name: string,
    description: string,
    mediaTypeId: number,
    file: File
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('CaseId', caseId.toString());
    formData.append('folderId', folderId.toString());
    formData.append('planTypeId', planTypeId ? planTypeId.toString() : '');
    formData.append('Name', name);
    formData.append('Description', description);
    formData.append('MediaTypeId', mediaTypeId.toString());
    formData.append('File', file);
    formData.append('blobName', file.name);

    return this.http
      .put<UploadResponse>(`${this.BASE_URL}/CaseAttachment/update`, formData, {
        params: { id: id.toString() },
      })
      .pipe(
        // tap(() => this.refreshAttachments(caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteAttachment(id: number): Observable<UploadResponse> {
    return this.http
      .delete<UploadResponse>(`${this.BASE_URL}/CaseAttachment/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getMediaTypes(searchTerm = ''): Observable<MediaTypeResponse> {
    return this.http
      .get<MediaTypeResponse>(`${this.BASE_URL}/ApplicationLookup`, {
        params: {
          TypeName: 'MediaType',
          SearchTerm: searchTerm,
        },
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private getExtensionFromMediaType(mediaTypeId: number): string {
    const mediaTypeMap: Record<number, string> = {
      1: '.pdf', // PDF files
      2: '.mp4', // Video files
      3: '.mp3', // Audio files
      4: '.docx', // Word documents
      5: '.xlsx', // Excel files
      6: '.pptx', // PowerPoint files
      7: '.wav', // Audio files
      8: '.tif', // Image files
      9: '.m4v', // Video files
      10: '.m4a', // Audio files
      11: '.ppt', // PowerPoint files (older format)
      12: '.xls', // Excel files (older format)
      13: '.doc', // Word documents (older format)
      14: '.tiff', // Image files
    };

    return mediaTypeMap[mediaTypeId] || '';
  }

  downloadAttachmentBlob(url: string): Observable<Blob> {
    const isAzureBlobUrl = url.includes('blob.core.windows.net');

    if (isAzureBlobUrl) {
      return from(
        fetch(url).then(response => {
          if (!response.ok) {
            throw new Error(
              `Failed to download file: ${response.status} ${response.statusText}`
            );
          }
          return response.blob();
        })
      ).pipe(catchError(error => this.errorHandler.handleError(error)));
    } else {
      return this.http
        .get(url, { responseType: 'blob' })
        .pipe(catchError(error => this.errorHandler.handleError(error)));
    }
  }

  downloadAttachmentsAsZip(
    attachments: Attachment[],
    zipFileName = 'attachments.zip'
  ): Observable<void> {
    const downloadRequests = attachments.map(attachment =>
      this.downloadAttachmentBlob(attachment.fileUrl).pipe(
        map(blob => ({ blob, name: attachment.name, attachment }))
      )
    );

    return forkJoin(downloadRequests).pipe(
      map(responses => {
        const zip = new JSZip();
        const usedFileNames = new Set<string>();

        responses.forEach(({ blob, name, attachment }) => {
          let extension = '';

          extension = this.getExtensionFromMediaType(attachment.mediaTypeId);

          if (!extension) {
            if (attachment.fileUrl.includes('blob.core.windows.net')) {
              const urlPath = attachment.fileUrl.split('?')[0];
              const pathParts = urlPath.split('/');
              const fileName = pathParts[pathParts.length - 1];

              const commonExtensions = [
                'mp4',
                'm4v',
                'm4a',
                'wav',
                'pptx',
                'ppt',
                'xlsx',
                'xls',
                'docx',
                'doc',
                'mp3',
                'pdf',
                'tif',
                'tiff',
              ];
              const lowerFileName = fileName.toLowerCase();

              for (const ext of commonExtensions) {
                if (lowerFileName.endsWith(`.${ext}`)) {
                  extension = `.${ext}`;
                  break;
                }
              }

              if (!extension && fileName.includes('.')) {
                const lastDotIndex = fileName.lastIndexOf('.');
                const possibleExt = fileName
                  .substring(lastDotIndex + 1)
                  .toLowerCase();
                if (possibleExt.length <= 5 && !possibleExt.includes(' ')) {
                  extension = `.${possibleExt}`;
                }
              }
            }
          }

          let cleanFileName = name.replace(/[/\\]/g, '_');

          if (
            !cleanFileName.toLowerCase().endsWith(extension.toLowerCase()) &&
            extension
          ) {
            cleanFileName = `${cleanFileName}${extension}`;
          }

          let finalFileName = cleanFileName;
          let counter = 1;
          while (usedFileNames.has(finalFileName.toLowerCase())) {
            const nameWithoutExt = extension
              ? cleanFileName.slice(0, -extension.length)
              : cleanFileName;
            finalFileName = `${nameWithoutExt}_${counter}${extension}`;
            counter++;
          }

          usedFileNames.add(finalFileName.toLowerCase());
          zip.file(finalFileName, blob);
        });

        zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
          saveAs(content, zipFileName);
        });
      }),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  // private refreshAttachments(caseId: number): void {
  //   this.getAttachments(caseId).subscribe();
  // }
}
